import {
  signInWithPopup,
  GoogleAuthProvider,
  User as FirebaseUser,
  signOut as firebaseSignOut,
  UserCredential,
} from "firebase/auth";
import { auth, googleProvider } from "../../utils/firebase";
import { useSocialLogin } from "./user.api";
import { enqueueSnackbar } from "notistack";
import { useAuth } from "../../hooks";
import { useLocation, useNavigate } from "react-router-dom";

export interface GoogleAuthResponse {
  user: FirebaseUser;
  credential?: any;
}

export const signInWithGoogle = async (): Promise<UserCredential | null> => {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    return result;
  } catch (error: any) {
    console.error("Google sign-in error:", error);
    throw error;
  }
};

export const signOutFromGoogle = async (): Promise<void> => {
  try {
    await firebaseSignOut(auth);
  } catch (error) {
    console.error("Google sign-out error:", error);
    throw error;
  }
};

export const useGoogleSignIn = () => {
  const { update: socialLogin, isLoading } = useSocialLogin({
    key: ["social_login"],
  });
  const { setUser, setAuthToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectPath = location.state?.redirect;

  const handleGoogleSignIn = async () => {
    try {
      // Sign in with Google via Firebase
      const result = await signInWithGoogle();

      if (!result?.user) {
        throw new Error("Google sign-in failed");
      }
      console.log("result", result);

      const { user } = result;

      // Get FCM token for notifications
      let fcmToken: string | null = null;
      try {
        if (Notification.permission !== "denied") {
          const permission = await Notification.requestPermission();
          if (permission === "granted") {
            const { getToken } = await import("firebase/messaging");
            const { messaging } = await import("../../utils/firebase");
            const { REACT_APP_NOTIFICATION_VAPID_KEY } = await import(
              "../../constants/config"
            );
            fcmToken = await getToken(messaging, {
              vapidKey: REACT_APP_NOTIFICATION_VAPID_KEY,
            });
          }
        }
      } catch (err) {
        console.error("FCM token error:", err);
      }

      // Get the ID token from Firebase
      const idToken = await user.getIdToken();

      await socialLogin(
        {
          sso_token: idToken,
          fcm_token: fcmToken,
          device_type: "web",
        },
        {
          onSuccess: (data) => {
            localStorage.setItem("access_token", data.data?.access_token || "");

            setAuthToken(data.data?.access_token || "");
            if (data.data?.user) {
              setUser(data.data.user);
            }

            // Check if user has a username, if not redirect to username setup
            if (data.data?.user && !data.data.user.username) {
              navigate("/add-username", {
                state: {
                  redirect: redirectPath ?? `/user/${data.data.user.id}`,
                },
              });
            } else {
              enqueueSnackbar("Login successful.", {
                variant: "success",
              });
              // Navigate to user dashboard
              navigate(redirectPath ?? `/user/${data.data?.user?.id}`);
            }
            localStorage.setItem("isGuestLite", "false");
          },
          onError: (apiErr: any) => {
            enqueueSnackbar(
              apiErr?.response?.data?.message || "Google sign-in failed",
              { variant: "error" }
            );
          },
        }
      );
    } catch (error: any) {
      console.error("Google sign-in error:", error);

      // Handle specific Firebase errors
      if (error.code === "auth/popup-closed-by-user") {
        enqueueSnackbar("Sign-in cancelled", { variant: "info" });
      } else if (error.code === "auth/popup-blocked") {
        enqueueSnackbar("Pop-up blocked. Please allow pop-ups for this site.", {
          variant: "warning",
        });
      } else {
        enqueueSnackbar(error.message || "Google sign-in failed", {
          variant: "error",
        });
      }
    }
  };

  return {
    handleGoogleSignIn,
    isLoading,
  };
};
